/**
 * App Lifecycle Manager
 * 
 * iOS/Android에서 앱이 백그라운드로 갈 때 메모리 사용량을 줄이기 위해
 * Firestore 구독을 관리합니다.
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

type UnsubscribeFunction = () => void;

class AppLifecycleManager {
    private subscriptions: Map<string, UnsubscribeFunction> = new Map();
    private isBackground = false;
    private listeners: ((isBackground: boolean) => void)[] = [];

    constructor() {
        if (Capacitor.isNativePlatform()) {
            this.setupAppStateListener();
        }
    }

    private setupAppStateListener() {
        App.addListener('appStateChange', ({ isActive }) => {
            this.isBackground = !isActive;
            console.log(`[AppLifecycle] App state changed: ${isActive ? 'foreground' : 'background'}`);

            // 백그라운드 진입 시 모든 구독 해제
            if (this.isBackground) {
                console.log(`[AppLifecycle] Unsubscribing ${this.subscriptions.size} subscriptions to save memory`);
                this.unsubscribeAll();
            }

            // 리스너들에게 상태 변경 알림
            this.listeners.forEach(listener => listener(this.isBackground));
        });
    }

    /**
     * 구독 등록 (백그라운드 진입 시 자동 해제됨)
     */
    registerSubscription(key: string, unsubscribe: UnsubscribeFunction) {
        // 기존 구독이 있으면 먼저 해제
        if (this.subscriptions.has(key)) {
            this.subscriptions.get(key)?.();
        }
        this.subscriptions.set(key, unsubscribe);
    }

    /**
     * 특정 구독 해제
     */
    unsubscribe(key: string) {
        const unsub = this.subscriptions.get(key);
        if (unsub) {
            unsub();
            this.subscriptions.delete(key);
        }
    }

    /**
     * 모든 구독 해제
     */
    unsubscribeAll() {
        this.subscriptions.forEach((unsub, key) => {
            try {
                unsub();
                console.log(`[AppLifecycle] Unsubscribed: ${key}`);
            } catch (e) {
                console.error(`[AppLifecycle] Error unsubscribing ${key}:`, e);
            }
        });
        this.subscriptions.clear();
    }

    /**
     * 백그라운드 상태 확인
     */
    get isInBackground() {
        return this.isBackground;
    }

    /**
     * 상태 변경 리스너 등록
     */
    onStateChange(listener: (isBackground: boolean) => void): UnsubscribeFunction {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    /**
     * 등록된 구독 수 확인
     */
    get subscriptionCount() {
        return this.subscriptions.size;
    }
}

// 싱글톤 인스턴스
export const appLifecycle = new AppLifecycleManager();
